<script setup>
import PhoneNumberInput from '@/components/PhoneNumberInput.vue'
import { reactive, ref, computed, onMounted } from 'vue'
import { post, get } from '@/api/Services'
import { useStore } from 'vuex'
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import CButton from '@/components/CButton.vue'

const store = useStore()
const loggeduser = computed(() => store.state.account.loggeduser)

const contact_email = ref('')
const phone = ref('')
const header = ref('')
const header_subtext = ref('')
const address = ref('')
const button_text = ref('')
const subjects = ref([])
const subjectsLoading = ref(true)
const countries = ref([])
const cities = ref([])
const selectedCountry = ref(null)
const cityLoading = ref(false)
const loading = ref(false)
const handlePhoneValidation = ref(false)

const contacts = computed(() => [
  {
    icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M20 10.999H22C22 5.869 18.127 2 12.99 2V4C17.052 4 20 6.943 20 10.999Z" fill="white"/>
<path d="M13 8.00024C15.103 8.00024 16 8.89724 16 11.0002H18C18 7.77524 16.225 6.00024 13 6.00024V8.00024ZM16.422 13.4432C16.2298 13.2686 15.9773 13.1754 15.7178 13.1835C15.4582 13.1915 15.212 13.3001 15.031 13.4862L12.638 15.9472C12.062 15.8372 10.904 15.4762 9.71198 14.2872C8.51998 13.0942 8.15898 11.9332 8.05198 11.3612L10.511 8.96724C10.6974 8.78637 10.8061 8.54006 10.8141 8.28045C10.8222 8.02083 10.7289 7.76828 10.554 7.57624L6.85898 3.51324C6.68402 3.3206 6.44086 3.20374 6.18113 3.1875C5.9214 3.17125 5.66557 3.2569 5.46798 3.42624L3.29798 5.28724C3.12509 5.46075 3.0219 5.69169 3.00798 5.93624C2.99298 6.18624 2.70698 12.1082 7.29898 16.7022C11.305 20.7072 16.323 21.0002 17.705 21.0002C17.907 21.0002 18.031 20.9942 18.064 20.9922C18.3085 20.9786 18.5393 20.8749 18.712 20.7012L20.572 18.5302C20.7414 18.3328 20.8273 18.077 20.8112 17.8173C20.7951 17.5576 20.6785 17.3143 20.486 17.1392L16.422 13.4432Z" fill="white"/>
</svg>`,
    title: phone.value,
    tag: 'phone',
  },
  {
    icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M22 4H2V20H22V4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="white"/>
</svg>`,
    title: contact_email.value,
    tag: 'email',
  },
  {
    icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 1.5C9.81276 1.50258 7.71584 2.3726 6.16923 3.91922C4.62261 5.46584 3.75259 7.56276 3.75001 9.75C3.74739 11.5374 4.33124 13.2763 5.41201 14.7C5.41201 14.7 5.63701 14.9963 5.67376 15.039L12 22.5L18.3293 15.0353C18.3623 14.9955 18.588 14.7 18.588 14.7L18.5888 14.6978C19.669 13.2747 20.2526 11.5366 20.25 9.75C20.2474 7.56276 19.3774 5.46584 17.8308 3.91922C16.2842 2.3726 14.1873 1.50258 12 1.5ZM12 12.75C11.4067 12.75 10.8266 12.5741 10.3333 12.2444C9.83995 11.9148 9.45543 11.4462 9.22837 10.8981C9.00131 10.3499 8.9419 9.74667 9.05765 9.16473C9.17341 8.58279 9.45913 8.04824 9.87869 7.62868C10.2982 7.20912 10.8328 6.9234 11.4147 6.80764C11.9967 6.69189 12.5999 6.7513 13.1481 6.97836C13.6962 7.20542 14.1648 7.58994 14.4944 8.08329C14.8241 8.57664 15 9.15666 15 9.75C14.999 10.5453 14.6826 11.3078 14.1202 11.8702C13.5578 12.4326 12.7954 12.749 12 12.75Z" fill="white"/>
</svg>`,
    title: address.value,
    tag: 'address',
  },
])
const socials = ref([])
const formElements = reactive([
  {
    label: 'First Name',
    type: 'text',
    name: 'first-name',
    model: '',
    required: true,
  },
  {
    label: 'Last Name',
    type: 'text',
    name: 'last-name',
    model: '',
    required: true,
  },
  {
    label: 'Email',
    type: 'email',
    name: 'email',
    model: '',
    required: true,
  },
  {
    label: 'Phone',
    type: 'phone',  
    name: 'phone',
    model: '',
    required: true,
  },
  {
    label: 'Country',
    type: 'text',
    name: 'country',
    model: '',
    required: true,
  },
  {
    label: 'City',
    type: 'text',
    name: 'city',
    model: '',
    required: true,
  },
  {
    label: 'Company',
    type: 'text',
    name: 'company',
    model: '',
    required: true,
  },
  {
    label: 'Company Size',
    type: 'dropdown',
    name: 'company-size',
    model: '',
    required: true,
    options: [
      { label: '1-10 employees', value: '1-10' },
      { label: '11-50 employees', value: '11-50' },
      { label: '51-200 employees', value: '51-200' },
      { label: '201-500 employees', value: '201-500' },
      { label: '501-1000 employees', value: '501-1000' },
      { label: '1000+ employees', value: '1000+' },
    ],
  },
  {
    label: 'Select Subject?',
    type: 'radio',
    name: 'subject',
    model: '',
    required: true,
    options: computed(() =>
      subjects.value.map((subject) => ({
        label: subject.description,
        value: subject.id,
      }))
    ),
  },
  {
    label: 'Message',
    type: 'textarea',
    name: 'message',
    model: '',
    required: true,
  },
])
const cmsData = ref([])

const rules = computed(() => {
  const validationRules = {}
  formElements.forEach((element) => {
    if (element.required) {
      validationRules[element.name] = { required }
      if (element.type === 'email') {
        validationRules[element.name].email = email
      }
    }
  })
  return validationRules
})

const formData = computed(() => {
  const data = {}
  formElements.forEach((element) => {
    data[element.name] = element.model
  })
  return data
})

const v$ = useVuelidate(rules, formData)

const toast = useToast()

const cms = async () => {
  const parameters = [
    {
      parameter_name: 'piv_page_path',
      parameter_value: '/contact-us',
    },
    {
      parameter_name: 'piv_tag_group',
      parameter_value: 'general',
    },
  ]

  await post('/Commons/Executer', {
    sqlId: 76,
    parameters: JSON.stringify(parameters),
  })
    .then((data) => {
      console.log(data)
      cmsData.value = data
      cmsData.value.map((item) => {
        switch (item.tag) {
          case 'header':
            header.value = item.html_content
            break
          case 'header_subtext':
            header_subtext.value = item.html_content
            break
          case 'email':
            contact_email.value = item.html_content
            break
          case 'phone':
            phone.value = item.html_content
            break
          case 'address':
            address.value = item.html_content
            break
          case 'social_list':
            socials.value = JSON.parse(item.list_content).sort(
              (a, b) => a.order_id - b.order_id
            )
            break
          case 'button_text':
            button_text.value = item.html_content
            break
        }
      })
    })
    .catch((err) => console.log(err))
}

const getSubjects = async () => {
  subjectsLoading.value = true
  await post('/Commons/Executer', {
    sqlId: 84,
    parameters: JSON.stringify([]),
  })
    .then((data) => {
      subjects.value = data
    })
    .catch((err) => console.log(err))
    .finally(() => {
      subjectsLoading.value = false
    })
}

const handleSubmit = async (event) => {
  event.preventDefault()

  const result = await v$.value.$validate()
  if (!result) {
    return
  }

  loading.value = true

  const parameters = [
    {
      parameter_name: 'pin_user_id',
      parameter_value: loggeduser.value?.id || null,
    },
    {
      parameter_name: 'piv_firstname',
      parameter_value: formElements.find((e) => e.name === 'first-name').model,
    },
    {
      parameter_name: 'piv_lastname',
      parameter_value: formElements.find((e) => e.name === 'last-name').model,
    },
    {
      parameter_name: 'piv_email',
      parameter_value: formElements.find((e) => e.name === 'email').model,
    },
    {
      parameter_name: 'piv_phone',
      parameter_value: formElements.find((e) => e.name === 'phone').model,
    },
    {
      parameter_name: 'piv_country',
      parameter_value: formElements.find((e) => e.name === 'country').model,
    },
    {
      parameter_name: 'piv_city',
      parameter_value: formElements.find((e) => e.name === 'city').model,
    },
    {
      parameter_name: 'piv_company',
      parameter_value: formElements.find((e) => e.name === 'company').model,
    },
    {
      parameter_name: 'piv_company_size',
      parameter_value: formElements.find((e) => e.name === 'company-size')
        .model,
    },
    {
      parameter_name: 'pin_subject_id',
      parameter_value: formElements.find((e) => e.name === 'subject').model,
    },
    {
      parameter_name: 'piv_message',
      parameter_value: formElements.find((e) => e.name === 'message').model,
    },
  ]

  try {
    await post('/Commons/Executer', {
      sqlId: 85,
      parameters: JSON.stringify(parameters),
    })

    // Clear form and reset validation
    formElements.forEach((element) => {
      element.model = ''
    })
    v$.value.$reset()

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Your message has been sent successfully!',
      life: 3000,
    })
  } catch (error) {
    console.error(error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'An error occurred while sending your message. Please try again.',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  Promise.all([cms(), getSubjects()])
})
</script>
<template>
  <div class="contact-container">
    <div
      class="contact-header text-center mb-5"
      v-if="cmsData[0]?.header && cmsData[0]?.header_subtext"
    >
      <h1 class="contact-title" v-if="cmsData[0]?.header">
        {{ cmsData[0]?.header }}
      </h1>
      <p class="contact-subtitle" v-if="cmsData[0]?.header_subtext">
        {{ cmsData[0]?.header_subtext }}
      </p>
    </div>
    <div class="contact-card">
      <div class="contact-info-wrapper">
        <div class="contact-info relative">
          <div class="d-flex flex-column justify-content-between h-100">
            <div class="contact-info-header">
              <div class="contact-info-title">{{ header }}</div>
              <div class="contact-info-subtitle">
                {{ header_subtext }}
              </div>
            </div>
            <div class="contact-details">
              <ul class="contact-list">
                <li
                  class="contact-item"
                  v-for="(contact, index) in contacts"
                  :key="index"
                >
                  <a
                    class="contact-link"
                    v-if="contact.tag == 'phone'"
                    :href="`tel:${contact.title}`"
                  >
                    <div v-html="contact.icon" class="contact-icon" />
                    <span class="contact-text">{{ contact.title }}</span>
                  </a>
                  <a
                    class="contact-link"
                    v-else-if="contact.tag == 'email'"
                    :href="`mailto:${contact.title}`"
                  >
                    <div v-html="contact.icon" class="contact-icon" />
                    <span class="contact-text">{{ contact.title }}</span>
                  </a>
                  <div class="contact-link" v-else>
                    <div v-html="contact.icon" class="contact-icon" />
                    <span class="contact-text">{{ contact.title }}</span>
                  </div>
                </li>
              </ul>
            </div>
            <div class="social-media">
              <ul class="social-list">
                <li
                  class="social-item"
                  v-for="(social, index) in socials"
                  :key="index"
                >
                  <router-link
                    class="social-link"
                    :to="social.html_body"
                    target="_blank"
                  >
                    <i :class="`pi pi-${social.icon_name}`" />
                  </router-link>
                </li>
              </ul>
            </div>
            <div class="info-vector">
              <svg
                width="208"
                height="242"
                viewBox="0 0 208 242"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="162.5"
                  cy="160.5"
                  r="134.5"
                  fill="white"
                  fill-opacity="0.12"
                />
                <circle
                  cx="69"
                  cy="69"
                  r="69"
                  fill="#FFF9F9"
                  fill-opacity="0.13"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div class="contact-form-wrapper">
        <form class="contact-form" @submit="handleSubmit">
          <div class="form-grid">
            <template v-for="(element, index) in formElements" :key="index">
              <div
                :class="`form-group ${
                  element.type === 'textarea' ? 'full-width' : ''
                }`"
                v-if="
                  element.type != 'radio' &&
                  element.type != 'dropdown' &&
                  element.type != 'phone'
                "
              >
                <span class="p-float-label">
                  <!-- Textarea -->
                  <Textarea
                    v-if="element.type === 'textarea'"
                    v-model="element.model"
                    class="w-100"
                    rows="5"
                    cols="30"
                    :class="{ 'p-invalid': v$[element.name]?.$error }"
                  />

                  <!-- InputText -->
                  <InputText
                    v-else
                    class="w-100"
                    :id="element.name"
                    :type="element.type"
                    v-model="element.model"
                    :class="{ 'p-invalid': v$[element.name]?.$error }"
                  />
                  <label :for="element.name">{{ element.label }}</label>
                </span>
                <small v-if="v$[element.name]?.$error" class="p-error">
                  {{ element.label }} is required
                </small>
              </div>

              <!-- Phone Number Input -->
              <div class="form-group" v-else-if="element.type === 'phone'">
                <PhoneNumberInput
                  v-model:phoneNumber="element.model"
                  :class="{ 'has-error': v$[element.name]?.$error && !handlePhoneValidation }"
                  @is-valid="handlePhoneValidation = $event"
                />
                <small v-if="v$[element.name]?.$error" class="p-error">
                  {{ element.label }} is required
                </small>
              </div>

              <!-- Dropdown -->
              <div class="form-group" v-else-if="element.type === 'dropdown'">
                <span class="p-float-label">
                  <Dropdown
                    v-model="element.model"
                    :options="element.options"
                    optionLabel="label"
                    optionValue="value"
                    :id="element.name"
                    class="w-100"
                    :class="{ 'p-invalid': v$[element.name]?.$error }"
                  />
                  <label :for="element.name">{{ element.label }}</label>
                </span>
                <small v-if="v$[element.name]?.$error" class="p-error">
                  {{ element.label }} is required
                </small>
              </div>

              <!-- RadioButton -->
              <div
                class="form-group full-width"
                v-else-if="element.type === 'radio'"
              >
                <label class="radio-label">{{ element.label }}</label>
                <div class="radio-group">
                  <template v-if="!subjectsLoading">
                    <div
                      v-for="(option, optionIndex) in subjects"
                      :key="optionIndex"
                      class="radio-option"
                    >
                      <RadioButton
                        :inputId="`${element.name}-${optionIndex}`"
                        v-model="element.model"
                        :name="element.name"
                        :value="option.id"
                        :class="{ 'p-invalid': v$[element.name]?.$error }"
                      />
                      <label
                        class="radio-option-label"
                        :for="`${element.name}-${optionIndex}`"
                        >{{ option.description }}</label
                      >
                    </div>
                  </template>
                  <div
                    v-else
                    v-for="n in 4"
                    :key="n"
                    class="radio-option-loading"
                  >
                    <Skeleton width="20px" height="20px" borderRadius="50%" />
                    <Skeleton width="150px" height="20px" />
                  </div>
                </div>
                <small v-if="v$[element.name]?.$error" class="p-error">
                  Please select a subject
                </small>
              </div>
            </template>
          </div>
          <div class="form-submit">
            <CButton
              type="submit"
              :label="button_text"
              variant="solid"
              color="secondary"
              class="submit-button"
              :loading="loading"
            />
          </div>
        </form>
      </div>
    </div>
  </div>
  <Toast />
</template>

<style lang="scss" scoped>
// Base styles
.contact-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.contact-header {
  margin-bottom: 3rem;
}

.contact-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #444444;
  margin-bottom: 1rem;
}

.contact-subtitle {
  font-size: 1.125rem;
  font-weight: 500;
  color: #2c2c2c;
  max-width: 700px;
  margin: 0 auto;
}

// Card layout
.contact-card {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 10px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0px 0px 60px 30px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
}

// Contact info section
.contact-info-wrapper {
  padding: 10px;
  
  @media (max-width: 992px) {
    padding: 10px 10px 0 10px;
  }
}

.contact-info {
  position: relative;
  background-color: #015cac;
  border-radius: 10px;
  padding: 40px;
  height: 100%;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 576px) {
    padding: 20px;
  }
}

.contact-info-header {
  margin-bottom: 2rem;
}

.contact-info-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  
  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
}

.contact-info-subtitle {
  font-size: 1rem;
  color: #c9c9c9;
  line-height: 1.5;
}

.contact-details {
  margin-bottom: 2rem;
}

.contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.contact-item {
  margin-bottom: 1.25rem;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.contact-link {
  display: flex;
  align-items: center;
  color: #ffffff;
  text-decoration: none;
  font-size: 1rem;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
}

.contact-icon {
  margin-right: 1rem;
  flex-shrink: 0;
  
  @media (max-width: 576px) {
    margin-right: 0.75rem;
  }
}

.contact-text {
  word-break: break-word;
}

.social-media {
  margin-top: 2rem;
}

.social-list {
  display: flex;
  gap: 1rem;
  list-style: none;
  padding: 0;
  margin: 0;
}

.social-item {
  width: 34px;
  height: 34px;
}

.social-link {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fc6c2a;
  color: #ffffff;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #ffffff;
    color: #015cac;
  }
  
  i {
    font-size: 1rem;
  }
}

.info-vector {
  position: absolute;
  bottom: -5%;
  right: -10%;
  z-index: 0;
  max-width: 80%;
  
  @media (max-width: 576px) {
    opacity: 0.5;
  }
}

// Form section
.contact-form-wrapper {
  padding: 10px;
  
  @media (max-width: 992px) {
    padding: 0 10px 10px 10px;
  }
}

.contact-form {
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 576px) {
    padding: 20px;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
}

.form-group {
  &.full-width {
    grid-column: span 2;
    
    @media (max-width: 768px) {
      grid-column: span 1;
    }
  }
}

// Radio button styling
.radio-label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #011c2a;
  font-size: 0.875rem;
}

.radio-group {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.radio-option-label {
  margin: 0;
  cursor: pointer;
  font-size: 0.875rem;
  color: #444444;
}

.radio-option-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

// Submit button
.form-submit {
  display: flex;
  justify-content: flex-end;
  position: relative;
}

.submit-button {
  min-width: 120px;
  z-index: 1;
}

.button-vector {
  position: absolute;
  right: 90px;
  bottom: -5px;
  z-index: 0;
  opacity: 0.8;
  
  @media (max-width: 576px) {
    right: 50px;
    bottom: -15px;
    transform: scale(0.8);
  }
}

// Input styling
.p-float-label {
  margin-bottom: 0.25rem;
  
  input,
  textarea,
  .p-dropdown {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #dbdbdb;
    font-size: 0.95rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    
    &:hover:not(.p-invalid) {
      border-color: #bdbdbd;
    }
    
    &:focus:not(.p-invalid) {
      border-color: #015cac;
      box-shadow: 0 0 0 2px rgba(1, 92, 172, 0.1);
    }
    
    &.p-invalid {
      border-color: #ff5757;
    }
  }
  
  label {
    color: #666;
  }
}

.p-error {
  color: #ff5757;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
}

// Responsive tweaks
@media (max-width: 992px) {
  .contact-info {
    padding-bottom: 60px;
  }
}

@media (max-width: 576px) {
  .contact-title {
    font-size: 1.75rem;
  }
  
  .contact-subtitle {
    font-size: 1rem;
  }
}
</style>
